import * as Phaser from 'phaser'
import { animationKeyFromVector, type Facing } from './Player'
import { screenToWorld } from '../engine/iso'

export interface PathPoint {
  x: number
  y: number
}

// Half the player speed so crossings feel unhurried and the depth-sort canary
// is easier to observe visually per CONTEXT.md.
const NPC_SPEED = 64

/**
 * Walks along a polyline by stepPx along the current segment. Ping-pongs at
 * both ends (direction flips, remaining step distance is carried into the new
 * direction). Pure: no Phaser, no side effects — test with unit vectors only.
 *
 * Returns fresh state (never mutates input).
 */
export function stepAlongPath(
  path: PathPoint[],
  pos: PathPoint,
  direction: 1 | -1,
  segmentIndex: number,
  stepPx: number,
): { pos: PathPoint; direction: 1 | -1; segmentIndex: number } {
  if (path.length < 2) {
    return { pos: { x: pos.x, y: pos.y }, direction, segmentIndex }
  }

  let remaining = stepPx
  let curPos = { x: pos.x, y: pos.y }
  let curDir: 1 | -1 = direction
  let curSeg = segmentIndex

  // Guard against pathological loops if stepPx massively exceeds path length.
  let guard = path.length * 4

  while (remaining > 1e-9 && guard-- > 0) {
    // Target endpoint of the current segment given direction.
    // direction=+1 walks from path[curSeg] toward path[curSeg+1]
    // direction=-1 walks from path[curSeg+1] toward path[curSeg]
    const toIdx = curDir === 1 ? curSeg + 1 : curSeg
    const to = path[toIdx]!
    const dx = to.x - curPos.x
    const dy = to.y - curPos.y
    const dist = Math.hypot(dx, dy)

    if (dist < 1e-9) {
      // Already at the endpoint — advance to next segment or flip at end.
      if (curDir === 1) {
        if (curSeg + 1 >= path.length - 1) {
          // At last point; flip direction, stay on last segment.
          curDir = -1
          // Keep curSeg at path.length - 2 so we walk path[seg+1] → path[seg].
        } else {
          curSeg += 1
        }
      } else {
        if (curSeg <= 0) {
          curDir = 1
          // Stay on segment 0.
        } else {
          curSeg -= 1
        }
      }
      continue
    }

    if (remaining < dist) {
      // Consume full remaining step within this segment.
      const t = remaining / dist
      curPos = { x: curPos.x + dx * t, y: curPos.y + dy * t }
      remaining = 0
      // Snap exactly on arrival — guards against fp drift compounding.
      if (Math.abs(curPos.x - to.x) < 1e-9 && Math.abs(curPos.y - to.y) < 1e-9) {
        curPos = { x: to.x, y: to.y }
      }
    } else {
      // Walk to the endpoint and carry leftover distance into next segment.
      curPos = { x: to.x, y: to.y }
      remaining -= dist
      if (curDir === 1) {
        if (curSeg + 1 >= path.length - 1) {
          curDir = -1 // flip at final point
          // Segment index stays at path.length - 2 so next step walks backward.
        } else {
          curSeg += 1
        }
      } else {
        if (curSeg <= 0) {
          curDir = 1 // flip at start point
        } else {
          curSeg -= 1
        }
      }
    }
  }

  return { pos: curPos, direction: curDir, segmentIndex: curSeg }
}

/**
 * NPC — scripted polyline walker.
 *
 * Invariants (mirror Player):
 *  - Foot-anchored origin (0.5, 1.0)
 *  - tileX / tileY maintained via screenToWorld so WorldScene.depthSortAll() reads
 *    tile-space coordinates, never raw screen arithmetic (ENGN-02)
 *  - Animation keys always atlas-prefixed (npc-a-walk-s, npc-b-idle-e, …) via
 *    the shared animationKeyFromVector helper — zero collision with player anims.
 */
export class NPC extends Phaser.Physics.Arcade.Sprite {
  public tileX = 0
  public tileY = 0
  private path: PathPoint[]
  private segmentIndex = 0
  private direction: 1 | -1 = 1
  private lastFacing: Facing = 's'
  private readonly atlasKey: 'npc-a' | 'npc-b'

  constructor(scene: Phaser.Scene, atlasKey: 'npc-a' | 'npc-b', path: PathPoint[]) {
    if (path.length < 2) {
      throw new Error(`NPC path must have >=2 points, got ${path.length}`)
    }
    const start = path[0]!
    super(scene, start.x, start.y, atlasKey, 'idle-s')
    scene.add.existing(this)
    scene.physics.add.existing(this)
    this.setOrigin(0.5, 1.0)
    this.setSize(20, 10)
    this.setOffset(6, 38)
    this.setCollideWorldBounds(false)
    this.path = path
    this.atlasKey = atlasKey
    const t = screenToWorld(start.x, start.y)
    this.tileX = t.tx
    this.tileY = t.ty
  }

  tick(dt: number): void {
    const stepPx = NPC_SPEED * (dt / 1000)
    const prev = { x: this.x, y: this.y }
    const next = stepAlongPath(this.path, prev, this.direction, this.segmentIndex, stepPx)
    this.direction = next.direction
    this.segmentIndex = next.segmentIndex
    this.setPosition(Math.round(next.pos.x), Math.round(next.pos.y))
    const v = { x: next.pos.x - prev.x, y: next.pos.y - prev.y }
    const anim = animationKeyFromVector(v, this.lastFacing, this.atlasKey)
    if (anim.walking) this.lastFacing = anim.facing
    if (this.anims.currentAnim?.key !== anim.key) this.anims.play(anim.key, true)
    // ENGN-02: tile coords synced from the actual render position.
    const t = screenToWorld(this.x, this.y)
    this.tileX = t.tx
    this.tileY = t.ty
  }
}
